/**
 * Curated route → view-chunk prefetch registry (navigation perf).
 *
 * Each route's page is loaded through a lazy `clientPage` boundary, so its view
 * chunk is fetched only when the route mounts — `next/link` prefetches the route
 * payload, but not that lazy chunk. This registry lets `<Link>` warm the view
 * chunk on hover/focus (navigation intent) so reaching a frequently-used section
 * is instant instead of showing the loading skeleton.
 *
 * Keyed by exact resolved pathname (these are static main-nav destinations). The
 * import specifiers MUST match the corresponding `app/<route>/page.tsx` loaders so the
 * bundler emits — and the browser caches — the very same chunk the route needs.
 * Limited to a handful of high-traffic routes on purpose; dynamic/parametrized
 * routes are not prefetched here.
 */
const VIEW_LOADERS: Record<string, () => Promise<unknown>> = {
  '/': () => import('@/components/views/HomeView'),
  '/pooling': () => import('@/components/views/pooling/Search/SearchView'),
  '/communities/discover': () =>
    import('@/components/views/circles/CirclesDiscoverView'),
  '/contact': () => import('@/components/views/ContactView'),
  '/team': () => import('@/components/views/TeamView'),
  '/my/materials': () =>
    import('@/components/views/pooling/my/material/MyMaterialsView'),
  '/my/communities': () =>
    import('@/components/views/circles/my/MyCommunitiesView'),
  '/my/bookings/requests': () =>
    import('@/components/views/pooling/my/booking/MyBookingsRequestsView'),
}

const prefetched = new Set<string>()

/**
 * Warm the lazy view chunk for `href` (fire-and-forget, at most once per path).
 * No-op for paths absent from the registry or outside the browser.
 */
export function prefetchView(href: string): void {
  if (typeof window === 'undefined') return
  const path = href.split(/[?#]/)[0]
  if (prefetched.has(path)) return
  const load = VIEW_LOADERS[path]
  if (!load) return
  prefetched.add(path)
  void load().catch(() => {
    // Allow a retry on a later hover if the chunk failed to load.
    prefetched.delete(path)
  })
}
